import { __decorate } from "tslib";
import { ChameleonElementMixin } from '../common/mixin';
import { attr, DOM, html, observable, repeat } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { getLocalTimeZone, getWeeksInMonth, isSameDay, isSameMonth, parseDate, startOfMonth, today, } from '@internationalized/date';
import { SVG_CHEVRON_LEFT, SVG_CHEVRON_RIGHT } from '@getgo/chameleon-icons';
import { constructCurrentMonth, constructNextMonth, constructPreviousMonth, formatNumber, getAriaLabelForDay, getDatesInWeek, getTabIndexForDay, getWeekDays, isAfterMax, isBeforeMin, isDayInactive, isLocaleValid, isTimezoneValid, } from '../common/CalendarUtils';
export const calendarTemplate = html `
  <chameleon-typography class="title" variant="heading-small">
    <slot name="header"></slot>
  </chameleon-typography>
  <div class="header">
    <chameleon-icon-button
      size="small"
      aria-label="${(x) => isLocaleValid(x.locale) && constructPreviousMonth(x.locale)}"
      @click=${(x) => x.handlePreviousMonthClick()}
      ><chameleon-svg>${SVG_CHEVRON_LEFT}</chameleon-svg></chameleon-icon-button
    >
    <h2>${(x) => x.month}</h2>
    <chameleon-button size="small" class="today-button" variant="tertiary" @click=${(x) => x.handleTodayButtonClick()}
      >${(x) => isLocaleValid(x.locale) && constructCurrentMonth(x.locale)}</chameleon-button
    >
    <chameleon-icon-button
      size="small"
      aria-label="${(x) => isLocaleValid(x.locale) && constructNextMonth(x.locale)}"
      @click=${(x) => x.handleNextMonthClick()}
      ><chameleon-svg>${SVG_CHEVRON_RIGHT}</chameleon-svg></chameleon-icon-button
    >
  </div>
  <table>
    <thead>
      <tr>
        ${repeat((x) => x.weekDays, html `<td>${(x) => x}</td>`)}
      </tr>
    </thead>

    <tbody>
      ${repeat((x) => x.weeks, html `
          <tr>
            ${repeat((x) => x, html `
                <td role="gridcell" class="cell">
                  <span
                    tabindex="${(x, c) => getTabIndexForDay(x, c.parentContext.parent.focusedDate)}"
                    role="button"
                    aria-label="${(x, c) => getAriaLabelForDay(x, c.parentContext.parent.locale, c.parentContext.parent.timezone)}"
                    data-value="${(x) => x.toString()}"
                    class="day
                        ${(x, c) => c.parentContext.parent.date && isSameDay(x, c.parentContext.parent.date) ? 'selected' : ''}
                        ${(x, c) => (isSameDay(x, today(c.parentContext.parent.timezone)) ? 'today' : '')}
                        ${(x, c) => (isDayInactive(x, c.parentContext.parent.focusedDate, c.parentContext.parent.min, c.parentContext.parent.max) ? 'inactive' : '')}
                        "
                    @click=${(x, c) => c.parentContext.parent.handleDayClick(x)}
                    @keydown=${(x, c) => c.parentContext.parent.handleDayKeyDown(c.event, x)}
                  >
                    ${(x, c) => formatNumber(c.parentContext.parent.locale, x.day)}
                  </span>
                </td>
              `)}
          </tr>
        `)}
    </tbody>
  </table>
`;
export class CalendarComponent extends ChameleonElementMixin(FoundationElement) {
    constructor() {
        super(...arguments);
        this.locale = navigator.language;
        this.timezone = getLocalTimeZone();
        this.weeks = [];
        this.weekDays = [];
        this.month = '';
        /**
         * Represents the currently focused or focusable date.
         * Scenarios where this can be different than `this.date`:
         * - When there's no selected date, this.date is undefined, but this is initialized to the current day
         * - When there's a selected date, but the user has gone to the next/previous month.
         * */
        this.focusedDate = this.date ? this.date : today(this.timezone);
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this.value && isLocaleValid(this.locale) && isTimezoneValid(this.timezone)) {
            this.month = this.getMonth();
            this.weeks = this.getWeeks();
            this.weekDays = getWeekDays(this.timezone, this.locale);
        }
    }
    valueChanged() {
        if (this.value) {
            try {
                const date = parseDate(this.value);
                const isBeforeMin = !this.min ? false : parseDate(this.min).compare(date) > 0;
                const isAfterMax = !this.max ? false : parseDate(this.max).compare(date) < 0;
                if (isBeforeMin || isAfterMax) {
                    return;
                }
                this.month = this.getMonth();
                this.weeks = this.getWeeks();
                this.weekDays = getWeekDays(this.timezone, this.locale);
                this.date = date;
                this.focusedDate = this.date;
            }
            catch { }
        }
    }
    localeChanged() {
        if (!isLocaleValid(this.locale)) {
            return;
        }
        this.weeks = this.getWeeks();
        this.weekDays = getWeekDays(this.timezone, this.locale);
    }
    timezoneChanged() {
        if (!isTimezoneValid(this.timezone)) {
            return;
        }
        this.weeks = this.getWeeks();
        this.weekDays = getWeekDays(this.timezone, this.locale);
    }
    getWeeks(base) {
        const n = base ? base : this.value ? parseDate(this.value) : today(this.timezone);
        const normalized = n.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        return [...new Array(getWeeksInMonth(startOfMonth(normalized), this.locale)).keys()].map((i) => {
            return getDatesInWeek(this.locale, i, startOfMonth(normalized));
        });
    }
    getMonth(base) {
        const date = base ? base : this.value ? parseDate(this.value) : today(this.timezone);
        return new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(date.toDate(this.timezone));
    }
    handleDayClick(date) {
        this.selectDay(date);
    }
    handleNextMonthClick() {
        this.focusedDate = this.focusedDate.add({ months: 1 });
        this.refreshDaysInCalendar();
    }
    handlePreviousMonthClick() {
        this.focusedDate = this.focusedDate.subtract({ months: 1 });
        this.refreshDaysInCalendar();
    }
    handleTodayButtonClick() {
        const currentDay = today(this.timezone);
        const isBeforeMin = !this.min ? false : parseDate(this.min).compare(currentDay) > 0;
        const isAfterMax = !this.max ? false : parseDate(this.max).compare(currentDay) < 0;
        if (isBeforeMin || isAfterMax) {
            return;
        }
        this.selectDay(today(this.timezone));
        this.refreshDaysInCalendar();
    }
    handleDayKeyDown(e, day) {
        switch (e.code) {
            case 'ArrowUp':
                this.moveToPrevWeek(day);
                // Prevent scrolling
                return false;
            case 'ArrowDown':
                this.moveToNextWeek(day);
                // Prevent scrolling
                return false;
            case 'ArrowLeft':
                this.moveToPrevDay(day);
                break;
            case 'ArrowRight':
                this.moveToNextDay(day);
                break;
            case 'Enter':
                this.selectDay(day);
                break;
            case 'Space':
                this.selectDay(day);
                break;
            default:
                break;
        }
        // We have to return true to prevent fast calling .preventDefault on the event
        // we need the default behaviour so the Tab keypress properly moves focus to the next element
        return true;
    }
    moveToNextWeek(currentDay) {
        const prev = this.focusedDate;
        const newDate = currentDay.add({ weeks: 1 });
        if (isAfterMax(newDate, this.max)) {
            return;
        }
        this.focusedDate = newDate;
        if (!isSameMonth(prev, this.focusedDate)) {
            this.refreshDaysInCalendar();
        }
        DOM.queueUpdate(() => {
            const nextDayEl = this.shadowRoot?.querySelector(`[data-value="${this.focusedDate.toString()}"]`);
            nextDayEl.focus();
        });
    }
    moveToPrevWeek(currentDay) {
        const prev = this.focusedDate;
        const newDate = currentDay.subtract({ weeks: 1 });
        if (isBeforeMin(newDate, this.min)) {
            return;
        }
        this.focusedDate = newDate;
        if (!isSameMonth(prev, this.focusedDate)) {
            this.refreshDaysInCalendar();
        }
        DOM.queueUpdate(() => {
            const nextDayEl = this.shadowRoot?.querySelector(`[data-value="${this.focusedDate.toString()}"]`);
            nextDayEl.focus();
        });
    }
    moveToPrevDay(currentDay) {
        const prev = this.focusedDate;
        const newDate = currentDay.subtract({ days: 1 });
        if (isBeforeMin(newDate, this.min)) {
            return;
        }
        this.focusedDate = newDate;
        if (!isSameMonth(prev, this.focusedDate)) {
            this.refreshDaysInCalendar();
        }
        DOM.queueUpdate(() => {
            const nextDayEl = this.shadowRoot?.querySelector(`[data-value="${this.focusedDate.toString()}"]`);
            nextDayEl.focus();
        });
    }
    moveToNextDay(currentDay) {
        const prev = this.focusedDate;
        const newDate = currentDay.add({ days: 1 });
        if (isAfterMax(newDate, this.max)) {
            return;
        }
        this.focusedDate = newDate;
        if (!isSameMonth(prev, this.focusedDate)) {
            this.refreshDaysInCalendar();
        }
        DOM.queueUpdate(() => {
            const nextDayEl = this.shadowRoot?.querySelector(`[data-value="${this.focusedDate.toString()}"]`);
            nextDayEl.focus();
        });
    }
    selectDay(day) {
        this.date = day;
        this.focusedDate = day;
        this.value = day.toString();
        this.$emit('ch-calendar-change', { date: day.toDate(this.timezone), customDate: day });
    }
    refreshDaysInCalendar() {
        this.weeks = this.getWeeks(this.focusedDate);
        this.month = this.getMonth(this.focusedDate);
    }
}
__decorate([
    attr
], CalendarComponent.prototype, "locale", void 0);
__decorate([
    attr
], CalendarComponent.prototype, "timezone", void 0);
__decorate([
    attr
], CalendarComponent.prototype, "value", void 0);
__decorate([
    attr
], CalendarComponent.prototype, "min", void 0);
__decorate([
    attr
], CalendarComponent.prototype, "max", void 0);
__decorate([
    observable
], CalendarComponent.prototype, "weeks", void 0);
__decorate([
    observable
], CalendarComponent.prototype, "weekDays", void 0);
__decorate([
    observable
], CalendarComponent.prototype, "month", void 0);
__decorate([
    observable
], CalendarComponent.prototype, "date", void 0);
__decorate([
    observable
], CalendarComponent.prototype, "focusedDate", void 0);
